// Recently viewed products, kept on this device only (localStorage).
//
//  * `recentlyViewed`: the last few product objects (legacy rail data).
//  * `anum360.viewHistory`: the last 50 product ids (+ category id), most
//    recent first. Public catalogue ids only (no personal data); sent as
//    `seen` / `cats` to GET /products/for-you so the home "For you" feed
//    follows what this shopper looked at, signed in or not.
// Every storage access is wrapped: private windows and blocked storage
// just mean no history.

const STORAGE_KEY = 'recentlyViewed';
const MAX_ITEMS = 12;

export const HISTORY_KEY = 'anum360.viewHistory';
export const MAX_HISTORY = 50;
const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

const readHistory = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((e) => e && typeof e.id === 'string' && ID_RE.test(e.id))
      .map((e) => ({ id: e.id, cat: typeof e.cat === 'string' && ID_RE.test(e.cat) ? e.cat : null }))
      .slice(0, MAX_HISTORY);
  } catch {
    return [];
  }
};

/** Records a product view in the id history (most recent first, ≤ 50). */
export const recordProductView = (product) => {
  const id = product?.id;
  if (typeof id !== 'string' || !ID_RE.test(id)) return;
  const cat = typeof product.category_id === 'string' ? product.category_id : null;
  try {
    const next = [{ id, cat }, ...readHistory().filter((e) => e.id !== id)].slice(0, MAX_HISTORY);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  } catch { /* storage full or blocked: no history */ }
};

/** Viewed product ids, most recent first (≤ 50). */
export const getRecentlyViewedIds = () => readHistory().map((e) => e.id);

/** Distinct category ids of the viewed products, most recent first (≤ 10). */
export const getRecentlyViewedCategories = (max = 10) => {
  const out = [];
  readHistory().forEach((e) => {
    if (e.cat && !out.includes(e.cat) && out.length < max) out.push(e.cat);
  });
  return out;
};

export const addToRecentlyViewed = (product) => {
  recordProductView(product);
  try {
    const existing = getRecentlyViewed();
    // Remove if already exists
    const filtered = existing.filter(p => p.id !== product.id);
    // Add to beginning
    const updated = [product, ...filtered].slice(0, MAX_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    if(process.env.NODE_ENV!=="production")
    console.error('Failed to save recently viewed:', error);
  }
};

export const getRecentlyViewed = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    if(process.env.NODE_ENV!=="production")
    console.error('Failed to get recently viewed:', error);
    return [];
  }
};

export const clearRecentlyViewed = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(HISTORY_KEY);
  } catch (error) {
    if(process.env.NODE_ENV!=="production")
    console.error('Failed to clear recently viewed:', error);
  }
};

export default {
  addToRecentlyViewed,
  getRecentlyViewed,
  clearRecentlyViewed,
  recordProductView,
  getRecentlyViewedIds,
  getRecentlyViewedCategories,
};
